import React, { useEffect, useRef, useState } from "react";
import "./styles.css"; // Import the CSS file for styles
import { getToken } from "firebase/app-check";
import { appCheck } from "./firebase";


function loadRazorpayScript(src) {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

const initialState = {
  name: "",
  parentName: "",
  contact: "",
  email: "",
  country: "",
  state: "",
  conditions: [],
  otherCondition: "",
  behaviours: [],
  otherbehaviours: "",
  age: 3,
  planType: "single",
  therapyPlanPreference: "",
};

export default function HomePlanForm() {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState(false);
  const contactInputRef = useRef(null);
  const countrySelectRef = useRef(null);
  const stateSelectRef = useRef(null);

  // NEW: popup metadata
  const [popupType, setPopupType] = useState(""); // "success" | "error"
  const [popupMessage, setPopupMessage] = useState("");
  const [savedDocId, setSavedDocId] = useState(null);
  const [savedPaymentId, setSavedPaymentId] = useState(null);

  // --- new state for contact validation ---
  const [contactError, setContactError] = useState("");
  const [contactTouched, setContactTouched] = useState(false);
  const [stateError, setStateError] = useState("");
  const [stateTouched, setStateTouched] = useState(false);
  const [countryError, setCountryError] = useState("");
  const [countryTouched, setCountryTouched] = useState(false);
  const [therapyPlanError, setTherapyPlanError] = useState("");
  const [therapyPlanTouched, setTherapyPlanTouched] = useState(false);

  // form-level errors for missing/invalid fields
  const [formErrors, setFormErrors] = useState({});

  const conditionsList = [
    "Autism",
    "ADHD",
    "Other",
  ];
  const behaviourList = [
    "Stubborn",
    "Hyperactivity",
    "Impulsivity",
    "Excessive Jumping",
    "Speech Delay",
    "Hitting",
    "Stimming",
    "Self talking",
    "No turn taking",
    "Self injurious behavior",
    "Aggression",
    "Other",
  ];
  const therapyPlanOptions = [
    {value: "occupational", label: "Occupational Therapy"},
    {value: "speech", label: "Speech Therapy"},
    {value: "behaviour", label: "Behaviour Therapy"},
    {value: "blend", label: "Blend of All Therapies"},
    {value: "undecided", label: "I don't know, help me decide on call"},
  ];
  const planTypeOptions = [
    {
      value: "single",
      title: "Single plan",
      description: "One personalised home support plan",
    },
    {
      value: "combo",
      title: "4-plan combo",
      badge: "20% off",
      description: "Four plans bundled with 20% discount shared over 4 weeks",
    },
  ];

  const pricingConfig = {
    single: {
      IN: { amountPaise: 149900, currency: "INR", display: "₹1,499" },
      INTL: { amountPaise: 1999, currency: "USD", display: "$19.99" },
    },
    combo: {
      IN: { amountPaise: 479900, currency: "INR", display: "₹4,799" },
      INTL: { amountPaise: 6399, currency: "USD", display: "$63.99" },
    },
  };

  const getPlanPricing = (planType = "single", country = "IN") => {
    const normalizedPlan = pricingConfig[planType] ? planType : "single";
    const tier = country === "IN" ? "IN" : "INTL";
    return pricingConfig[normalizedPlan][tier];
  };
  const indianStates = [
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
    "Andaman and Nicobar Islands",
    "Chandigarh",
    "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi",
    "Jammu and Kashmir",
    "Ladakh",
    "Lakshadweep",
    "Puducherry",
    "Other",
  ];

  const validateContact = (value) => {
    // Require leading +countryCode then number. Examples: +91xxxxxxxxxx, +1 5551234567
    const re = /^\+\d{1,3}\s?\d{6,14}$/;
    if (!value || value.trim() === "") {
      return {
        valid: false,
        msg: "Contact is required (include country code, e.g. +91).",
      };
    }
    if (!re.test(value.trim())) {
      return {
        valid: false,
        msg: "Enter full number with country code, e.g. +91 9876543210.",
      };
    }
    return { valid: true, msg: "" };
  };

  const validateStateSelection = (countryValue, stateValue) => {
    if (countryValue !== "IN") {
      return {valid: true, msg: ""};
    }
    if (!stateValue || stateValue.trim() === "") {
      return {
        valid: false,
        msg: "Please choose your state (or select Other).",
      };
    }
    return {valid: true, msg: ""};
  };

  const validateCountry = (value) => {
    if (!value || value.trim() === "") {
      return {
        valid: false,
        msg: "Please select your country.",
      };
    }
    return {valid: true, msg: ""};
  };

  const validateTherapyPlan = (value) => {
    if (!value) {
      return {
        valid: false,
        msg: "Please choose a therapy plan option.",
      };
    }
    return {valid: true, msg: ""};
  };

  // Generic form validator that covers required fields
  const validateForm = () => {
    const errors = {};
    const required = ["name", "parentName", "contact", "email", "country", "therapyPlanPreference"];
    required.forEach((key) => {
      const v = form[key];
      if (v === undefined || v === null || (typeof v === "string" && v.trim() === "")) {
        errors[key] = "Required";
      }
    });

    if (form.country === "IN") {
      if (!form.state || form.state.trim() === "") {
        errors.state = "Required";
      }
    }

    const contactVal = validateContact(form.contact);
    if (!contactVal.valid) errors.contact = contactVal.msg;

    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) errors.email = "Enter a valid email";

    if (Object.keys(errors).length) {
      errors._global = "Please fill the required fields before proceeding.";
      return { valid: false, errors };
    }
    return { valid: true, errors: {} };
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    // toggle behaviour/condition checkbox arrays
    if (name === "conditions" || name === "behaviours") {
      const current = form[name] || [];
      const newVals = checked ? [...current, value] : current.filter((c) => c !== value);
      setForm({ ...form, [name]: newVals });
      return;
    }

    // handle range (age) as number
    if (name === "age") {
      setForm({ ...form, age: Number(value) });
      return;
    }

    if (name === "country") {
      const nextStateValue = value === "IN" ? form.state : "";
      setForm({ ...form, country: value, state: nextStateValue });
      const countryValidation = validateCountry(value);
      setCountryError(countryValidation.msg);
      setCountryTouched(true);
      const {msg} = validateStateSelection(value, nextStateValue);
      setStateError(msg);
      if (value !== "IN") {
        setStateTouched(false);
      }
      return;
    }

    if (name === "state") {
      setForm({ ...form, state: value });
      const {msg} = validateStateSelection(form.country, value);
      setStateError(msg);
      return;
    }

    if (name === "therapyPlanPreference") {
      setForm({...form, therapyPlanPreference: value});
      const validation = validateTherapyPlan(value);
      setTherapyPlanError(validation.msg);
      setTherapyPlanTouched(true);
      return;
    }

    // default for other inputs
    setForm({ ...form, [name]: value });

    // validate contact as user types
    if (name === "contact") {
      const { msg } = validateContact(value);
      setContactError(msg);
    }
  };

  // validate on blur to show error only after user leaves field
  const handleContactBlur = () => {
    setContactTouched(true);
    const { msg } = validateContact(form.contact);
    setContactError(msg);
  };

  const handleStateBlur = () => {
    setStateTouched(true);
    const {msg} = validateStateSelection(form.country, form.state);
    setStateError(msg);
  };

  const handleCountryBlur = () => {
    setCountryTouched(true);
    const {msg} = validateCountry(form.country);
    setCountryError(msg);
  };

  const handleTherapyPlanBlur = () => {
    setTherapyPlanTouched(true);
    const {msg} = validateTherapyPlan(form.therapyPlanPreference);
    setTherapyPlanError(msg);
  };

  useEffect(() => {
    if (!loading) {
      return;
    }
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      const warningMessage =
        "Your payment is still being processed. Please stay on this page until you see a success or error message.";
      event.returnValue = warningMessage;
      return warningMessage;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [loading]);

  // updated handler with robust save and popup behavior
  const handlePayment = async (e) => {
    e.preventDefault();

    // clear previous errors
    setFormErrors({});

    // generic validation before any async/payment work
    const validation = validateForm();
    if (!validation.valid) {
      setFormErrors(validation.errors);
      // focus first missing field
      const firstMissing = Object.keys(validation.errors).find((k) => k !== "_global");
      try {
        if (firstMissing === "contact" && contactInputRef.current) {
          contactInputRef.current.focus();
          contactInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (firstMissing === "country" && countrySelectRef.current) {
          countrySelectRef.current.focus();
          countrySelectRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (firstMissing === "state" && stateSelectRef.current) {
          stateSelectRef.current.focus();
          stateSelectRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (firstMissing) {
          const el = document.getElementsByName(firstMissing)[0];
          if (el && typeof el.focus === "function") {
            el.focus();
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
      } catch (_) {}

      setPopupType("error");
      setPopupMessage(validation.errors._global || "Please complete the required fields.");
      setPopup(true);
      return;
    }

    const countryValidation = validateCountry(form.country);
    if (!countryValidation.valid) {
      setCountryTouched(true);
      setCountryError(countryValidation.msg);
      if (countrySelectRef.current) {
        countrySelectRef.current.focus();
        countrySelectRef.current.scrollIntoView({behavior: "smooth", block: "center"});
      }
      return;
    }

    const therapyValidation = validateTherapyPlan(form.therapyPlanPreference);
    if (!therapyValidation.valid) {
      setTherapyPlanTouched(true);
      setTherapyPlanError(therapyValidation.msg);
      const therapySection = document.getElementById("therapy-plan-section");
      if (therapySection) {
        therapySection.scrollIntoView({behavior: "smooth", block: "center"});
      }
      return;
    }

    const contactValidation = validateContact(form.contact);
    if (!contactValidation.valid) {
      setContactTouched(true);
      setContactError(contactValidation.msg);
      if (contactInputRef.current) {
        contactInputRef.current.focus();
        contactInputRef.current.scrollIntoView({behavior: "smooth", block: "center"});
      }
      return;
    }

    const stateValidation = validateStateSelection(form.country, form.state);
    if (!stateValidation.valid) {
      if (form.country === "IN") {
        setStateTouched(true);
        setStateError(stateValidation.msg);
        if (stateSelectRef.current) {
          stateSelectRef.current.focus();
          stateSelectRef.current.scrollIntoView({behavior: "smooth", block: "center"});
        }
      }
      return;
    }

    setLoading(true);

    // Determine if user is returning (check localStorage for now)
    // In production, this could be checked against the backend/webhook database
    const submittedEmails = JSON.parse(
      localStorage.getItem("submitted_emails") || "[]"
    );
    // const isReturningUser = submittedEmails.includes(form.email);
    const isReturningUser = true;
    //hardcoded for returning user to disable free plan option
    // Determine currency and amount based on country and selected plan
    const { amountPaise, currency: razorpayCurrency } = getPlanPricing(
      form.planType,
      form.country,
    );

    const razorpayKey =
      process.env.REACT_APP_RAZORPAY_KEY_ID ||
      process.env.REACT_APP_RAZORPAY_KEY_ID_TEST ||
      "";

    if (!razorpayKey) {
      setLoading(false);
      alert(
        "Razorpay key is missing. Add REACT_APP_RAZORPAY_KEY_ID_TEST or REACT_APP_RAZORPAY_KEY_ID to your .env and restart the dev server."
      );
      return;
    }

    const ok = await loadRazorpayScript("https://checkout.razorpay.com/v1/checkout.js");
    if (!ok) {
      setLoading(false);
      alert("Failed to load Razorpay SDK. Check your network or try again later.");
      return;
    }

    if (typeof window === "undefined" || !window.Razorpay) {
      setLoading(false);
      alert("Razorpay SDK loaded but window.Razorpay is not available.");
      return;
    }

  // cleanup any stale marker
  try { localStorage.removeItem("razorpay_success"); } catch(_) {}

  let safetyTimer = null;
  let pollTimer = null;
  let currentOrderId = "";
  let paymentResolved = false;
    const SAFETY_TIMEOUT_MS = 180 * 1000; // 3 minutes
    const POLL_INTERVAL_MS = 800; // poll for localStorage success marker

    // compute delivery deadline (ISO) = now + 7 days
    const computeDeliveryDeadline = () => {
      const d = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
      return d.toISOString();
    };

    // shared post-payment processing helper (extract so both handler & poll can use)
    const clearSafetyMechanisms = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
      if (safetyTimer) {
        clearTimeout(safetyTimer);
        safetyTimer = null;
      }
    };

    const processSuccessfulPayment = async (paymentId) => {
      paymentResolved = true;
      clearSafetyMechanisms();
      if (!paymentId) {
        setPopupType("error");
        setPopupMessage(
          "Payment failed or is still pending. If money was deducted but payment did not succeed, please reach out to our team for assistance.",
        );
        setPopup(true);
        setLoading(false);
        return;
      }

  try {
    // Mark this email as submitted
    const submittedEmails = JSON.parse(
      localStorage.getItem("submitted_emails") || "[]"
    );
    if (!submittedEmails.includes(form.email)) {
      submittedEmails.push(form.email);
      localStorage.setItem("submitted_emails", JSON.stringify(submittedEmails));
    }

    const record = {
      ...form,
      country: form.country || "NA",
      paymentId,
      paymentStatus: "paid",
      amountPaise,
      currency: razorpayCurrency,
      deliveryDeadline: computeDeliveryDeadline(),
      paymentDate: new Date().toISOString(), // Capture payment timestamp
      createdAt: new Date().toISOString(),
      planType: form.planType,
      orderId: currentOrderId || null,
      state: form.country === "IN" ? (form.state || "NA") : "NA",
    };

    // Call backend API
    const endpointUrl = process.env.REACT_APP_SUBMIT_HOMEPLAN_ENDPOINT || "http://localhost:5001/customized-home-plans/us-central1/submitHomePlan";
    
    // Build headers - do NOT include server secrets in the client bundle.
    // The server should validate App Check tokens (we attach App Check token here).
    const headers = { "Content-Type": "application/json" };

    // Attach App Check token (if available). If token fetch fails, the server
    // will reject the request once enforcement is enabled.
    try {
      const ac = await getToken(appCheck, /* forceRefresh */ false);
      if (ac && ac.token) {
        headers["X-Firebase-AppCheck"] = ac.token;
        try {
          // log token length only (do not print token itself)
          if (process.env.NODE_ENV !== 'production') {
            console.log("App Check token fetched (length):", ac.token.length);
          }
        } catch (_) {}
      } else {
        console.warn("App Check token fetch returned empty token or null (no header will be sent)");
      }
    } catch (e) {
      // Intentionally swallow — in development you may not have a token.
      console.warn("App Check token fetch failed (continuing):", e?.message || e);
    }

    const response = await fetch(endpointUrl, {
      method: "POST",
      headers,
      body: JSON.stringify(record),
    });

    const result = await response.json().catch(() => ({}));

    if (response.ok && result.success) {
      setSavedPaymentId(paymentId);
      setPopupType("success");
      setPopupMessage(
        `Payment successful (ID: ${paymentId}). Our team will call you within 24 hours to confirm details. You will also receive an email confirmation shortly.`
      );
      setForm(initialState);
      setContactError("");
      setContactTouched(false);
      setCountryError("");
      setCountryTouched(false);
      setStateError("");
      setStateTouched(false);
  setTherapyPlanError("");
  setTherapyPlanTouched(false);
      // After successful save and email, redirect to explore courses after 3s
      setTimeout(() => {
        try {
          window.location.href = "https://www.behaviourtherapy.in/collections";
        } catch (_) {}
      }, 3000);
    } else {
      // Log detailed error info
      const errorMsg = result.error || 'Unknown error';
      setPopupType("error");
      setPopupMessage(
        `Payment succeeded but saving details failed (${response.status}: ${errorMsg}). Our team has been notified and will follow up manually.`
      );
    }

    setPopup(true);
  } catch (err) {
    setPopupType("error");
    setPopupMessage(
      "Payment succeeded but an unexpected error occurred while saving details. Our team will follow up manually."
    );
    setPopup(true);
  } finally {
    setLoading(false);
  }
};


    let checkoutOptions = null;

    const orderEndpoint =
      process.env.REACT_APP_CREATE_RAZORPAY_ORDER_ENDPOINT ||
      "http://localhost:5001/customized-home-plans/us-central1/createRazorpayOrder";

    const orderHeaders = { "Content-Type": "application/json" };
    try {
      const tokenResult = await getToken(appCheck, /* forceRefresh */ false);
      if (tokenResult?.token) {
        orderHeaders["X-Firebase-AppCheck"] = tokenResult.token;
      }
    } catch (tokenErr) {
      console.warn(
        "App Check token fetch failed for order creation:",
        tokenErr?.message || tokenErr,
      );
    }

    if (!orderHeaders["X-Firebase-AppCheck"]) {
      setLoading(false);
      setPopupType("error");
      setPopupMessage("Unable to verify the app. Please refresh and try again.");
      setPopup(true);
      return;
    }

    const notesPayload = {
      childName: form.name,
      age: form.age,
      parentName: form.parentName || "",
      email: form.email,
      contact: form.contact,
      planType: form.planType,
      country: form.country || "NA",
      state: form.country === "IN" ? (form.state || "NA") : "NA",
      returningUser: isReturningUser ? "yes" : "no",
      conditions: (form.conditions || []).slice(0, 5).join(", ") || "NA",
      behaviours: (form.behaviours || []).slice(0, 5).join(", ") || "NA",
      therapyPlanPreference: form.therapyPlanPreference || "NA",
      course_name: `Customized Home Plan(${form.planType})`
    };

    try {
      const orderResponse = await fetch(orderEndpoint, {
        method: "POST",
        headers: orderHeaders,
        body: JSON.stringify({
          amountPaise,
          currency: razorpayCurrency,
          notes: notesPayload,
        }),
      });

      const orderJson = await orderResponse.json().catch(() => ({}));
      if (!orderResponse.ok || !orderJson?.order?.id) {
        const message = orderJson?.error || "Unable to create payment order.";
        throw new Error(message);
      }
      currentOrderId = orderJson.order.id;
      checkoutOptions = {
        key: razorpayKey,
        amount: amountPaise,
        currency: razorpayCurrency,
        order_id: currentOrderId,
        name: "",
        description: "Home Plan Fee",
        notes: notesPayload,
        handler: function (response) {
          try {
            if (!response || !response.razorpay_payment_id) {
              setPopupType("error");
              setPopupMessage("Payment completed but response unexpected. We'll verify and contact you.");
              setPopup(true);
              setLoading(false);
              return;
            }
            try {
              localStorage.setItem("razorpay_success", JSON.stringify({
                paymentId: response.razorpay_payment_id,
                signature: response.razorpay_signature || "",
                orderId: response.razorpay_order_id || "",
              }));
            } catch (_) {}
            processSuccessfulPayment(response.razorpay_payment_id);
          } catch (err) {
            setLoading(false);
          }
        },
        prefill: {
          name: form.name,
          email: form.email,
          contact: form.contact,
        },
        theme: {color: "#ff4da6"},
        modal: {
          ondismiss: function () {
            clearSafetyMechanisms();
            setLoading(false);
          },
        },
      };
    } catch (orderErr) {
      setLoading(false);
      setPopupType("error");
      setPopupMessage(
        "Unable to start payment right now. Please refresh and try again.",
      );
      setPopup(true);
      return;
    }

    try {
      const rzp = new window.Razorpay(checkoutOptions);

      rzp.on && rzp.on("payment.failed", function (response) {
        paymentResolved = true;
        clearSafetyMechanisms();
        try { localStorage.removeItem("razorpay_success"); } catch(_) {}
        setPopupType("error");
        setPopupMessage("Payment failed or was cancelled. Please try again.");
        setPopup(true);
        setLoading(false);
      });

      rzp.on && rzp.on("payment.success", function (response) {
        // Close modal after 2 seconds instead of waiting for default 5 seconds
        setTimeout(() => {
          rzp.close();
        }, 2000);
      });

      rzp.open();

      pollTimer = setInterval(() => {
        try {
          const marker = localStorage.getItem("razorpay_success");
          if (marker) {
            clearSafetyMechanisms();
            try { localStorage.removeItem("razorpay_success"); } catch(_) {}
            // Extract payment ID from stored JSON
            const paymentData = JSON.parse(marker);
            processSuccessfulPayment(paymentData.paymentId);
          }
        } catch (err) {
          // Silent catch
        }
      }, POLL_INTERVAL_MS);

      safetyTimer = setTimeout(() => {
        if (paymentResolved) {
          return;
        }
        clearSafetyMechanisms();
        try { localStorage.removeItem("razorpay_success"); } catch(_) {}
        setLoading(false);
        setPopupType("notice");
        setPopupMessage(
          "We're still waiting for Razorpay to confirm your payment. Please keep this page open; if money was deducted you will receive confirmation shortly.",
        );
        setPopup(true);
      }, SAFETY_TIMEOUT_MS);
    } catch (err) {
      alert("Payment initiation failed. Check console for details.");
      setLoading(false);
    }
  };

  const priceInfo = getPlanPricing(form.planType, form.country);

  return (
    <div className="page-bg">
      {/* Top-center quick links (replaces page header) */}
      <div
        style={{
          position: "fixed",
          top: 12,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1200,
          display: "flex",
          gap: 12,
          alignItems: "center",
        }}
      >
        <a
          href="https://www.appointment.behaviourtherapy.in"
          target="_blank"
          rel="noopener noreferrer"
          className="link"
          style={{
            background: "#fff",
            padding: "8px 10px",
            borderRadius: 6,
            boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
            textDecoration: "none",
            color: "#222",
            fontSize: 14,
          }}
        >
          Book a consultation
        </a>
        <a
          href="https://www.behaviourtherapy.in/collections"
          target="_blank"
          rel="noopener noreferrer"
          className="link"
          style={{
            background: "#fff",
            padding: "8px 10px",
            borderRadius: 6,
            boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
            textDecoration: "none",
            color: "#222",
            fontSize: 14,
          }}
        >
          Explore courses
        </a>
      </div>
      <div className="form-card form-card--wide">
        <div className="brand">
          <img src="/logo.png" alt="logo" className="brand-logo" />
        </div>
        <h2 className="card-title">Book Your Personalized Home Support Plan</h2>

        <form onSubmit={handlePayment} className="form-contents">
          <label className="label">Child's Name *</label>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            className={`input ${formErrors.name ? "input--error" : ""}`}
          />
          {formErrors.name && (
            <div className="field-error" role="alert" aria-live="polite">
              {formErrors.name}
            </div>
          )}
          <div className="age-row">
            <label className="label">
              Child's Age: <strong>{form.age}</strong>
            </label>
            <input
              type="range"
              name="age"
              min="1"
              max="18"
              value={form.age}
              onChange={handleChange}
              className="range"
            />
          </div>
          <label className="label">Parent/Guardian Name *</label>
          <input
            name="parentName"
            value={form.parentName}
            onChange={handleChange}
            required
            className={`input ${formErrors.parentName ? "input--error" : ""}`}
          />
          {formErrors.parentName && (
            <div className="field-error" role="alert" aria-live="polite">
              {formErrors.parentName}
            </div>
          )}
            

          <label className="label">Contact *</label>
          <input
            name="contact"
            value={form.contact}
            onChange={handleChange}
            onBlur={handleContactBlur} // <-- new: validate on blur
            required
            className="input"
            ref={contactInputRef}
            aria-invalid={Boolean(contactError)}
            aria-describedby="contact-error"
            placeholder="e.g., +91 9876543210"
          />
          {/* show inline error when touched or present */}
          {(contactTouched || contactError) && contactError && (
            <div
              id="contact-error"
              className="field-error"
              role="alert"
              aria-live="polite"
            >
              {contactError}
            </div>
          )}

          <div className="info-strip">
            Please include country code e.g., +91, +1
          </div>

          <label className="label">Your Country *</label>
          <select
            name="country"
            value={form.country}
            onChange={handleChange}
            onBlur={handleCountryBlur}
            className="input"
            ref={countrySelectRef}
            required
            aria-invalid={Boolean(countryError)}
            aria-describedby="country-error"
          >
            <option value="" disabled>
              Select your country
            </option>
            <option value="IN">India</option>
            <option value="CA">Canada</option>
            <option value="US">United States</option>
            <option value="GB">United Kingdom</option>
            <option value="AU">Australia</option>
            <option value="CN">China</option>
            <option value="JP">Japan</option>
            <option value="FR">France</option>
            <option value="DE">Germany</option>
            <option value="IT">Italy</option>
            <option value="ES">Spain</option>
            <option value="CH">Switzerland</option>
            <option value="AT">Austria</option>
            <option value="NL">Netherlands</option>
            <option value="BE">Belgium</option>
            <option value="DK">Denmark</option>
            <option value="SE">Sweden</option>
            <option value="NO">Norway</option>
            <option value="FI">Finland</option>
            <option value="AE">United Arab Emirates</option>
            <option value="PK">Pakistan</option>
            <option value="OT">Other</option>
          </select>
          {(countryTouched || countryError) && countryError && (
            <div
              id="country-error"
              className="field-error"
              role="alert"
              aria-live="polite"
            >
              {countryError}
            </div>
          )}

          {form.country === "IN" && (
            <div style={{marginTop: 12}}>
              <label className="label">State *</label>
              <select
                name="state"
                value={form.state}
                onChange={handleChange}
                onBlur={handleStateBlur}
                className="input"
                required
                ref={stateSelectRef}
                aria-invalid={Boolean(stateError)}
                aria-describedby="state-error"
              >
                <option value="" disabled>
                  Select your state
                </option>
                {indianStates.map((stateName) => (
                  <option key={stateName} value={stateName}>
                    {stateName}
                  </option>
                ))}
              </select>
              {(stateTouched || stateError) && stateError && (
                <div
                  id="state-error"
                  className="field-error"
                  role="alert"
                  aria-live="polite"
                >
                  {stateError}
                </div>
              )}
            </div>
          )}

          

          <label className="label">Email *</label>
          <input
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
            className={`input ${formErrors.email ? "input--error" : ""}`}
          />
          {formErrors.email && (
            <div className="field-error" role="alert" aria-live="polite">
              {formErrors.email}
            </div>
          )}

          <label className="label">Diagnosed Condition(s)</label>

          <div className="chips-row" role="list">
            {conditionsList.map((cond) => (
              <label key={cond} className="chip-wrap" role="listitem">
                <input
                  type="checkbox"
                  name="conditions"
                  value={cond}
                  checked={form.conditions.includes(cond)}
                  onChange={handleChange}
                  className="chip-input"
                />
                <span className="chip" aria-hidden="true">
                  {cond}
                </span>
              </label>
            ))}
          </div>

          {form.conditions.includes("Other") && (
            <>
              <label className="label">Other Condition</label>
              <input
                name="otherCondition"
                value={form.otherCondition}
                onChange={handleChange}
                className="input"
              />
            </>
          )}

          <br />
          <label className="label">Identified Behavioural Issues</label>
          <div className="chips-row" role="list">
            {behaviourList.map((behav) => (
              <label key={behav} className="chip-wrap" role="listitem">
                <input
                  type="checkbox"
                  name="behaviours"
                  value={behav}
                  checked={form.behaviours.includes(behav)}
                  onChange={handleChange}
                  className="chip-input"
                />
                <span className="chip" aria-hidden="true">
                  {behav}
                </span>
              </label>
            ))}
          </div>

          {form.behaviours.includes("Other") && (
            <>
              <label className="label">Other Behaviour</label>
              <input
                name="otherbehaviours"
                value={form.otherbehaviours}
                onChange={handleChange}
                className="input"
              />
            </>
          )}

          <div id="therapy-plan-section" style={{margin: "18px 0"}}>
            <label
              className="label"
              id="therapy-plan-label"
            >
              Which therapy plan do you want? *
            </label>
            <div
              className="chips-row"
              role="radiogroup"
              aria-labelledby="therapy-plan-label"
            >
              {therapyPlanOptions.map((option) => (
                <label
                  key={option.value}
                  htmlFor={`therapy-${option.value}`}
                  className="chip-wrap"
                  role="radio"
                  aria-checked={form.therapyPlanPreference === option.value}
                >
                  <input
                    type="radio"
                    className="chip-input"
                    id={`therapy-${option.value}`}
                    name="therapyPlanPreference"
                    value={option.value}
                    checked={form.therapyPlanPreference === option.value}
                    onChange={handleChange}
                    onBlur={handleTherapyPlanBlur}
                  />
                  <span className="chip">{option.label}</span>
                </label>
              ))}
            </div>
            {(therapyPlanTouched || therapyPlanError) && therapyPlanError && (
              <div className="field-error" role="alert" aria-live="polite" style={{marginTop: 6}}>
                {therapyPlanError}
              </div>
            )}
          </div>

          <div id="plan-type-section" style={{ margin: "18px 0" }}>
            <label className="label" id="plan-type-label">
              Select single or combo (combo means 4 weekly plans for a type chosen above)
            </label>
            <div
              className="chips-row"
              style={{ marginTop: 12 }}
              role="radiogroup"
              aria-labelledby="plan-type-label"
            >
              {planTypeOptions.map((option) => {
                const selected = form.planType === option.value;
                return (
                  <label
                    key={option.value}
                    htmlFor={`plan-${option.value}`}
                    className="chip-wrap"
                    role="radio"
                    aria-checked={selected}
                    style={{
                      flex: "1 1 calc(50% - 8px)",
                      display: "flex",
                      alignItems: "stretch",
                      minWidth: 0,
                    }}
                  >
                    <input
                      id={`plan-${option.value}`}
                      type="radio"
                      name="planType"
                      value={option.value}
                      checked={selected}
                      onChange={handleChange}
                      className="chip-input"
                    />
                    <span
                      className={`chip plan-card ${selected ? "plan-card--selected" : ""}`}
                    >
                      {option.badge && (
                        <span className="plan-card__badge">{option.badge}</span>
                      )}
                      <span className="plan-card__title">{option.title}</span>
                      <span className="plan-card__description">
                        {option.description}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="note-strip">
            Note: Once you complete the payment, our team will reach out to you with next steps and a delivery date.
          </div>

          {/* show global form error if present */}
          {formErrors._global && (
            <div className="field-error" role="alert" aria-live="polite" style={{ marginBottom: 8 }}>
              {formErrors._global}
            </div>
          )}

          {/* CTA - disable when loading or contact invalid */}
          <button
            type="submit"
            disabled={loading}
            className="cta"
          >
            {loading
              ? "Processing..."
              : priceInfo
              ? `Pay ${priceInfo.display} & Book`
              : "Pay & Book"}
          </button>
        </form>
      </div>

      {/* Popup UI */}
      {loading && !popup && (
        <div className="popup-backdrop" role="alertdialog" aria-live="assertive">
          <div className="popup" style={{textAlign: "center"}}>
            <h3>Processing payment…</h3>
            <p style={{marginBottom: 8}}>
              We're waiting for Razorpay to confirm your transaction. This may take up to a couple of minutes
              when banks are busy.
            </p>
            <p style={{fontSize: 13, color: "#555"}}>
              Please keep this tab open and don't refresh or close the window until you see a success or error message.
            </p>
          </div>
        </div>
      )}

      {popup && (
        <div className="popup-backdrop" onClick={() => setPopup(false)}>
          <div className="popup" onClick={(e) => e.stopPropagation()}>
            {popupType === "success" ? (
              <>
                <h3>Payment Successful</h3>
                <p>{popupMessage}</p>
                {savedPaymentId && <p style={{fontSize:12,color:"#444"}}>Payment ID: {savedPaymentId}</p>}
                <div style={{display:"flex",gap:10,justifyContent:"center",marginTop:12}}>
                  <button onClick={() => { setPopup(false); }} className="btn-small">Close</button>
                </div>
              </>
            ) : (
              <>
                <h3>{popupType === "error" ? "Payment Error" : "Notice"}</h3>
                <p>{popupMessage || "There was an issue. Please try again or contact support."}</p>
                <div style={{display:"flex",gap:10,justifyContent:"center",marginTop:12}}>
                  <button onClick={() => setPopup(false)} className="btn-small">Close</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
